import type { MouseEvent as ReactMouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import { CARD_CLASS } from "../styles";
import type { OtaScopeHotel } from "../types";

type Props = {
  title: string;
  subtitle?: string;
  data: OtaScopeHotel[];
  onDownload?: () => void;
  downloadLoading?: boolean;
};

type Row = {
  id: string;
  hotelId: string;
  hotelName: string;
  isBase: boolean;
  category: string;
  categoryLabel: string;
  platformKey: string;
  platformLabel: string;
  roomName: string;
  values: Array<number | null>;
};

type DisplayRow = Row & {
  showHotel: boolean;
  hotelRowSpan: number;
  showCategory: boolean;
  categoryRowSpan: number;
  showPlatform: boolean;
  platformRowSpan: number;
};

const PLATFORM_LABELS: Record<string, string> = {
  yandex: "Яндекс",
  tinkoff: "Т-Путешествия",
  tbank: "Т-Путешествия",
  ostrovok: "Островок",
  sutochno: "Суточно",
  traveline: "Сайт отеля",
  travelline: "Сайт отеля",
  alean: "Alean",
  booking: "Booking",
};

const PLATFORM_ORDER: Record<string, number> = {
  yandex: 1,
  tinkoff: 2,
  tbank: 2,
  ostrovok: 3,
  sutochno: 4,
  traveline: 5,
  travelline: 5,
  alean: 6,
  booking: 7,
};

const DATE_COLUMN_WIDTH = 108;
const STICKY_WIDTHS = {
  hotel: 150,
  category: 108,
  platform: 140,
  room: 310,
};
const MIN_COLUMN_WIDTH = 96;

const normalizePlatform = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "tbank") return "tinkoff";
  if (normalized === "travelline") return "traveline";
  return normalized;
};

const isMissingPrice = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === 0 || value === "0" || value === "none";

const formatPlatformLabel = (value: string) =>
  PLATFORM_LABELS[value] ??
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatCategoryLabel = (value: string) =>
  value
    ? value
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Без категории";

const formatHeaderDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat("ru-RU", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
};

const formatPrice = (value: number | null) => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("ru-RU");
};

const getPlatformOrder = (value: string) => PLATFORM_ORDER[value] ?? Number.MAX_SAFE_INTEGER;

const getHeatmapStyle = (value: number | null, min: number, max: number) => {
  if (value === null || value === undefined) return {};
  if (max <= min) return { backgroundColor: "rgba(148, 163, 184, 0.12)" };
  const ratio = (value - min) / (max - min);
  const hue = 95 - ratio * 70;
  return { backgroundColor: `hsla(${hue.toFixed(0)}, 80%, 67%, 0.72)` };
};

const buildDisplayRows = (sourceRows: Row[]): DisplayRow[] => {
  const rows = sourceRows.map((row) => ({
    ...row,
    showHotel: false,
    hotelRowSpan: 0,
    showCategory: false,
    categoryRowSpan: 0,
    showPlatform: false,
    platformRowSpan: 0,
  }));

  let hotelStart = 0;
  while (hotelStart < rows.length) {
    let hotelEnd = hotelStart + 1;
    while (hotelEnd < rows.length && rows[hotelEnd].hotelId === rows[hotelStart].hotelId) {
      hotelEnd += 1;
    }
    rows[hotelStart].showHotel = true;
    rows[hotelStart].hotelRowSpan = hotelEnd - hotelStart;

    let categoryStart = hotelStart;
    while (categoryStart < hotelEnd) {
      let categoryEnd = categoryStart + 1;
      while (
        categoryEnd < hotelEnd &&
        rows[categoryEnd].hotelId === rows[categoryStart].hotelId &&
        rows[categoryEnd].category === rows[categoryStart].category
      ) {
        categoryEnd += 1;
      }
      rows[categoryStart].showCategory = true;
      rows[categoryStart].categoryRowSpan = categoryEnd - categoryStart;

      let platformStart = categoryStart;
      while (platformStart < categoryEnd) {
        let platformEnd = platformStart + 1;
        while (
          platformEnd < categoryEnd &&
          rows[platformEnd].hotelId === rows[platformStart].hotelId &&
          rows[platformEnd].category === rows[platformStart].category &&
          rows[platformEnd].platformKey === rows[platformStart].platformKey
        ) {
          platformEnd += 1;
        }
        rows[platformStart].showPlatform = true;
        rows[platformStart].platformRowSpan = platformEnd - platformStart;
        platformStart = platformEnd;
      }

      categoryStart = categoryEnd;
    }

    hotelStart = hotelEnd;
  }

  return rows;
};

export default function BestPriceOtaScopeTable({
  title,
  subtitle,
  data,
  onDownload,
  downloadLoading = false,
}: Props) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    hotel: STICKY_WIDTHS.hotel,
    category: STICKY_WIDTHS.category,
    platform: STICKY_WIDTHS.platform,
    room: STICKY_WIDTHS.room,
  });
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const mapped = useMemo(() => {
    const dateSet = new Set<string>();
    data.forEach((hotel) => {
      hotel.rooms.forEach((room) => {
        room.prices.forEach((pricePoint) => {
          if (pricePoint.date) dateSet.add(pricePoint.date);
        });
      });
    });
    const dates = Array.from(dateSet).sort((left, right) => left.localeCompare(right));
    const dateIndexMap = new Map(dates.map((date, index) => [date, index]));
    const rows: Row[] = [];

    data.forEach((hotel) => {
      hotel.rooms.forEach((room) => {
        const platformMap = new Map<string, Array<number | null>>();
        room.prices.forEach((pricePoint) => {
          const index = dateIndexMap.get(pricePoint.date);
          if (index === undefined) return;
          Object.entries(pricePoint).forEach(([platformKey, rawValue]) => {
            if (platformKey === "date" || isMissingPrice(rawValue)) return;
            const normalizedPlatform = normalizePlatform(platformKey);
            const values = platformMap.get(normalizedPlatform) ?? Array.from({ length: dates.length }, () => null);
            values[index] = Number(rawValue);
            platformMap.set(normalizedPlatform, values);
          });
        });

        Array.from(platformMap.entries()).forEach(([platformKey, values]) => {
          rows.push({
            id: `${hotel.hotel_id}-${room.category}-${platformKey}-${room.room_name}`,
            hotelId: String(hotel.hotel_id),
            hotelName: hotel.hotel_name,
            isBase: Boolean(hotel.is_base),
            category: room.category,
            categoryLabel: formatCategoryLabel(room.category),
            platformKey,
            platformLabel: formatPlatformLabel(platformKey),
            roomName: room.room_name,
            values,
          });
        });
      });
    });

    const sortedRows = rows.sort((left, right) => {
      if (Number(left.isBase) !== Number(right.isBase)) {
        return Number(right.isBase) - Number(left.isBase);
      }
      const hotelCompare = left.hotelName.localeCompare(right.hotelName, "ru");
      if (hotelCompare !== 0) return hotelCompare;
      const categoryCompare = left.categoryLabel.localeCompare(right.categoryLabel, "ru");
      if (categoryCompare !== 0) return categoryCompare;
      const platformCompare = getPlatformOrder(left.platformKey) - getPlatformOrder(right.platformKey);
      if (platformCompare !== 0) return platformCompare;
      return left.roomName.localeCompare(right.roomName, "ru");
    });

    return {
      dates,
      rows: buildDisplayRows(sortedRows),
    };
  }, [data]);

  const handleResizeMove = useCallback((event: MouseEvent) => {
    const state = resizeRef.current;
    if (!state) return;
    const nextWidth = Math.max(MIN_COLUMN_WIDTH, state.startWidth + event.clientX - state.startX);
    setColumnWidths((current) => ({
      ...current,
      [state.key]: nextWidth,
    }));
  }, []);

  const stopResize = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", stopResize);
  }, [handleResizeMove]);

  const startResize = useCallback(
    (key: string, event: ReactMouseEvent<HTMLSpanElement>) => {
      resizeRef.current = {
        key,
        startX: event.clientX,
        startWidth: columnWidths[key] ?? DATE_COLUMN_WIDTH,
      };
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", stopResize);
    },
    [columnWidths, handleResizeMove, stopResize],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [handleResizeMove, stopResize]);

  const totalWidth =
    (columnWidths.hotel ?? STICKY_WIDTHS.hotel) +
    (columnWidths.category ?? STICKY_WIDTHS.category) +
    (columnWidths.platform ?? STICKY_WIDTHS.platform) +
    (columnWidths.room ?? STICKY_WIDTHS.room) +
    mapped.dates.reduce((sum, date) => sum + (columnWidths[`date:${date}`] ?? DATE_COLUMN_WIDTH), 0);

  const stickyCategoryLeft = columnWidths.hotel ?? STICKY_WIDTHS.hotel;
  const stickyPlatformLeft = stickyCategoryLeft + (columnWidths.category ?? STICKY_WIDTHS.category);
  const stickyRoomLeft = stickyPlatformLeft + (columnWidths.platform ?? STICKY_WIDTHS.platform);

  return (
    <section className={CARD_CLASS}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              disabled={downloadLoading}
              className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 lg:self-auto"
            >
              <Download size={14} />
              {downloadLoading ? "Скачиваем..." : "Скачать XLSX"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 max-h-[72vh] overflow-auto rounded-[28px] border border-slate-200 bg-white">
        <div style={{ minWidth: totalWidth }}>
          <table className="w-full border-separate border-spacing-0 text-sm">
            <colgroup>
              <col style={{ width: columnWidths.hotel ?? STICKY_WIDTHS.hotel }} />
              <col style={{ width: columnWidths.category ?? STICKY_WIDTHS.category }} />
              <col style={{ width: columnWidths.platform ?? STICKY_WIDTHS.platform }} />
              <col style={{ width: columnWidths.room ?? STICKY_WIDTHS.room }} />
              {mapped.dates.map((date) => (
                <col key={date} style={{ width: columnWidths[`date:${date}`] ?? DATE_COLUMN_WIDTH }} />
              ))}
            </colgroup>
            <thead>
              <tr className="bg-slate-100">
                <th className="sticky left-0 top-0 z-40 border border-slate-200 bg-slate-100 px-3 py-3 text-left text-[15px] font-semibold text-slate-900 shadow-[10px_0_18px_-16px_rgba(15,23,42,0.35)]">
                  <div className="relative pr-4">
                    Отель
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      onMouseDown={(event) => startResize("hotel", event)}
                      className="absolute right-[-10px] top-0 h-full w-4 cursor-col-resize touch-none"
                    >
                      <span className="absolute right-2 top-1/2 h-7 w-px -translate-y-1/2 bg-slate-300" />
                    </span>
                  </div>
                </th>
                <th className="sticky top-0 z-40 border border-slate-200 bg-slate-100 px-3 py-3 text-left text-[15px] font-semibold text-slate-900" style={{ left: stickyCategoryLeft }}>
                  <div className="relative pr-4">
                    Категория
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      onMouseDown={(event) => startResize("category", event)}
                      className="absolute right-[-10px] top-0 h-full w-4 cursor-col-resize touch-none"
                    >
                      <span className="absolute right-2 top-1/2 h-7 w-px -translate-y-1/2 bg-slate-300" />
                    </span>
                  </div>
                </th>
                <th className="sticky top-0 z-40 border border-slate-200 bg-slate-100 px-3 py-3 text-left text-[15px] font-semibold text-slate-900" style={{ left: stickyPlatformLeft }}>
                  <div className="relative pr-4">
                    Платформа
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      onMouseDown={(event) => startResize("platform", event)}
                      className="absolute right-[-10px] top-0 h-full w-4 cursor-col-resize touch-none"
                    >
                      <span className="absolute right-2 top-1/2 h-7 w-px -translate-y-1/2 bg-slate-300" />
                    </span>
                  </div>
                </th>
                <th className="sticky top-0 z-40 border border-slate-200 bg-slate-100 px-3 py-3 text-left text-[15px] font-semibold text-slate-900" style={{ left: stickyRoomLeft }}>
                  <div className="relative pr-4">
                    Название номера
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      onMouseDown={(event) => startResize("room", event)}
                      className="absolute right-[-10px] top-0 h-full w-4 cursor-col-resize touch-none"
                    >
                      <span className="absolute right-2 top-1/2 h-7 w-px -translate-y-1/2 bg-slate-300" />
                    </span>
                  </div>
                </th>
                {mapped.dates.map((date) => (
                  <th key={date} className="sticky top-0 z-30 border border-slate-200 bg-slate-100 px-2 py-3 text-center text-[14px] font-medium text-slate-700">
                    <div className="relative pr-4">
                      {formatHeaderDate(date)}
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize(`date:${date}`, event)}
                        className="absolute right-[-10px] top-0 h-full w-4 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-2 top-1/2 h-7 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mapped.rows.map((row, rowIndex) => {
                const stickyBackground = rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50";
                const rowBackground = row.isBase ? "bg-amber-50" : stickyBackground;
                const numericRowValues = row.values.filter((value): value is number => value !== null);
                const rowMin = numericRowValues.length ? Math.min(...numericRowValues) : 0;
                const rowMax = numericRowValues.length ? Math.max(...numericRowValues) : 0;
                return (
                  <tr key={row.id} className={row.isBase ? "bg-amber-50/30" : undefined}>
                    {row.showHotel && (
                      <td
                        rowSpan={row.hotelRowSpan}
                        className={[
                          "sticky left-0 z-20 border border-slate-200 px-3 py-3 align-top text-left text-[14px] font-medium break-words shadow-[10px_0_18px_-16px_rgba(15,23,42,0.35)]",
                          rowBackground,
                          row.isBase ? "text-amber-800" : "text-slate-700",
                        ].join(" ")}
                        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                      >
                        {row.hotelName}
                      </td>
                    )}

                    {row.showCategory && (
                      <td
                        rowSpan={row.categoryRowSpan}
                        className={[
                          "sticky z-20 border border-slate-200 px-3 py-3 align-top text-left text-[14px] text-slate-600",
                          rowBackground,
                        ].join(" ")}
                        style={{ left: stickyCategoryLeft }}
                      >
                        {row.categoryLabel}
                      </td>
                    )}

                    {row.showPlatform && (
                      <td
                        rowSpan={row.platformRowSpan}
                        className={[
                          "sticky z-20 border border-slate-200 px-3 py-3 align-top text-left text-[14px] text-slate-600",
                          rowBackground,
                        ].join(" ")}
                        style={{ left: stickyPlatformLeft }}
                      >
                        {row.platformLabel}
                      </td>
                    )}

                    <td
                      className={[
                        "sticky z-20 border border-slate-200 px-3 py-3 text-left text-[14px] text-slate-600",
                        rowBackground,
                      ].join(" ")}
                      style={{ left: stickyRoomLeft }}
                    >
                      {row.roomName}
                    </td>

                    {row.values.map((value, index) => (
                      <td
                        key={`${row.id}-${mapped.dates[index]}`}
                        className="border border-slate-200 px-2 py-3 text-right text-[14px] font-semibold text-slate-700"
                        style={getHeatmapStyle(value, rowMin, rowMax)}
                      >
                        {formatPrice(value)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
